import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';

// All-zero GUID. The admin device API treats null request fields as
// "don't change", so clearing an association (e.g. the vehicle) must be done
// by sending an explicit empty GUID rather than null.
export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

// Mirrors the admin device API's UpdateDeviceRequest. Every field is optional;
// the backend leaves any field it receives as null/undefined unchanged, so we
// only send the fields we actually want to modify.
export interface UpdateDeviceRequest {
  vehicleId?: string;
  actorId?: string;
  groupId?: string;
  assetId?: string;
  deviceId?: string;
  model?: string;
  statusId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AdminDeviceService {
  private readonly baseUrl = environment.deviceApi;

  constructor(private http: HttpClient) {}

  /**
   * Updates a single device via PUT /devices/{serialNumber}.
   * Only the fields present on the request are changed: the backend leaves any
   * field that is omitted/undefined or null unchanged. To actively clear a
   * value (rather than leave it as-is) send an explicit empty GUID (EMPTY_GUID).
   */
  updateDevice(
    serialNumber: string,
    request: UpdateDeviceRequest,
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/devices/${serialNumber}`,
      request,
    );
  }

  /**
   * Disassociates an LX1 (non-AVM) hub follower device from its vehicle by
   * clearing the vehicle association directly on the follower serial number.
   *
   * vehicleId is set to the empty GUID (not null) because the backend ignores
   * null fields — the empty GUID is what actively clears the association.
   *
   * actorId is the audit "who performed this change" field (the admin device
   * API stamps it onto the device and any sibling devices it cascades the
   * disassociation to); it defaults to Guid.Empty server-side. We send
   * EMPTY_GUID as a temporary placeholder.
   * TODO(MAV-3836 follow-up): send the acting user's id as actorId for audit
   * attribution once the user id is captured in Session.
   */
  disassociateHubDevice(serialNumber: string): Observable<void> {
    return this.updateDevice(serialNumber, {
      vehicleId: EMPTY_GUID,
      actorId: EMPTY_GUID,
    });
  }
}
